import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "apollo-react/components/Box";
import Typography from "apollo-react/components/Typography";
import CircularProgress from "apollo-react/components/CircularProgress";
import Button from "apollo-react/components/Button";
import Paper from "apollo-react/components/Paper";
import StatusCheck from "apollo-react-icons/StatusCheck";
import StatusNegative from "apollo-react-icons/StatusNegative";
import axiosInstance from "../../axiosInstance";
import { deleteDraft, getDraftById } from "../../utils/draftStorage";
import IQVIALogo from "../../assets/images/IQVIA-logo.png";
import "./PaymentProcessing.css";

const POLLING_INTERVAL = 1500; // 1.5 seconds
const MAX_POLLING_ATTEMPTS = 40; // Max 60 seconds of polling

const PaymentProcessing = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState("verifying"); // verifying, succeeded, failed
  const [message, setMessage] = useState(t("payment.verifying"));
  const [countdown, setCountdown] = useState(3);
  const [error, setError] = useState(null);

  const pollingAttemptsRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const hasProcessedRef = useRef(false);

  const orderId = searchParams.get("order_id") || searchParams.get("order");
  const successParam = searchParams.get("success");

  // Get temp draft info from localStorage
  const getTempDraft = useCallback(() => {
    const tempDraftKey = localStorage.getItem("payment_temp_draft_key");
    if (tempDraftKey) {
      return getDraftById(tempDraftKey);
    }
    return null;
  }, []);

  // Clear payment-related localStorage
  const clearPaymentStorage = useCallback(() => {
    const tempDraftKey = localStorage.getItem("payment_temp_draft_key");
    if (tempDraftKey) {
      deleteDraft(tempDraftKey);
    }
    localStorage.removeItem("payment_temp_draft_key");
    localStorage.removeItem("payment_order_id");
    localStorage.removeItem("payment_request_id");
  }, []);

  // Handle success redirect
  const handleSuccessRedirect = useCallback(() => {
    clearPaymentStorage();
    navigate("/customer/requests/my-requests?tab=0", { replace: true });
  }, [clearPaymentStorage, navigate]);

  // Handle failure redirect
  const handleFailureRedirect = useCallback(() => {
    const tempDraft = getTempDraft();
    const tempDraftKey = localStorage.getItem("payment_temp_draft_key");

    if (tempDraft && tempDraft.workflowVersionId) {
      const redirectUrl = `/customer/requests/new-requests/view/${
        tempDraft.workflowVersionId
      }?request_name=${encodeURIComponent(
        tempDraft.draftName || ""
      )}&draftId=${encodeURIComponent(tempDraftKey || "")}&paymentFailed=true`;

      // Clear payment session data but keep draft for form restoration
      localStorage.removeItem("payment_order_id");
      localStorage.removeItem("payment_request_id");
      // Note: Don't remove payment_temp_draft_key here - it will be cleared
      // by the new request page after loading the draft data

      navigate(redirectUrl, { replace: true });
    } else {
      // No draft available - clear everything and go to new requests
      clearPaymentStorage();
      navigate("/customer/requests/new-requests", { replace: true });
    }
  }, [getTempDraft, clearPaymentStorage, navigate]);

  // Poll for payment status
  const pollPaymentStatus = useCallback(async () => {
    if (!orderId || hasProcessedRef.current) return;

    try {
      pollingAttemptsRef.current += 1;

      const response = await axiosInstance.get(
        `/api/payments/status/${orderId}`
      );
      const { status: paymentStatus } = response.data.data;

      if (paymentStatus === "SUCCEEDED") {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus("succeeded");
        setMessage(t("payment.successMessage"));

        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleSuccessRedirect();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (paymentStatus === "FAILED") {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus("failed");
        setMessage(t("payment.declinedMessage"));

        // Start countdown for failure redirect
        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleFailureRedirect();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        // Timeout - assume failure
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus("failed");
        setMessage(t("payment.timeoutMessage"));
        setError("timeout");
      }
      // If still PENDING, continue polling
    } catch (err) {
      console.error("Error polling payment status:", err);
      if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        hasProcessedRef.current = true;
        clearInterval(pollingIntervalRef.current);
        setStatus("failed");
        setMessage(t("payment.networkErrorMessage"));
        setError("network");
      }
    }
  }, [orderId, handleSuccessRedirect, handleFailureRedirect, t]);

  // Check direct success param from Paymob redirect
  useEffect(() => {
    if (successParam === "true" && !hasProcessedRef.current) {
      // Paymob indicated success, but still verify with backend
      setMessage(t("payment.completedVerifyingMessage"));
    } else if (successParam === "false" && !hasProcessedRef.current) {
      hasProcessedRef.current = true;
      setStatus("failed");
      setMessage(t("payment.failedTitle"));

      setTimeout(() => {
        handleFailureRedirect();
      }, 2000);
      return;
    }
  }, [successParam, handleFailureRedirect, t]);

  // Start polling on mount
  useEffect(() => {
    if (!orderId) {
      setStatus("failed");
      setMessage(t("payment.invalidSessionMessage"));
      setError("no_order");
      return;
    }

    // Start polling
    pollingIntervalRef.current = setInterval(
      pollPaymentStatus,
      POLLING_INTERVAL
    );

    // Initial poll
    pollPaymentStatus();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [orderId, pollPaymentStatus, t]);

  const renderIcon = () => {
    switch (status) {
      case "succeeded":
        return <StatusCheck className="payment-status-icon success" />;
      case "failed":
        return <StatusNegative className="payment-status-icon error" />;
      default:
        return <CircularProgress variant="indeterminate" size={80} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "succeeded":
        return "#4caf50";
      case "failed":
        return "#f44336";
      default:
        return "#0768fd";
    }
  };

  return (
    <Box className="payment-processing-container">
      {/* Company Logo - Above Card */}
      <Box className="payment-logo-container">
        <img src={IQVIALogo} alt="IQVIA" className="payment-company-logo" />
        <Typography variant="caption" className="payment-logo-subtitle">
          Drug Registration System
        </Typography>
      </Box>

      <Paper elevation={3} className="payment-processing-card">
        <Box className="payment-processing-content">
          <Box
            className="payment-icon-container"
            style={{ color: getStatusColor() }}
          >
            {renderIcon()}
          </Box>

          <Typography variant="h4" className="payment-status-title">
            {status === "verifying" && t("payment.processingPaymentTitle")}
            {status === "succeeded" && t("payment.successTitle")}
            {status === "failed" && t("payment.failedTitle")}
          </Typography>

          <Typography variant="body1" className="payment-status-message">
            {message}
          </Typography>

          {status !== "verifying" && countdown > 0 && (
            <Typography variant="body2" className="payment-redirect-notice">
              {t("payment.redirectingIn", { seconds: countdown })}
            </Typography>
          )}

          {status === "verifying" && (
            <Typography variant="body2" className="payment-wait-notice">
              {t("payment.waitNotice")}
            </Typography>
          )}

          {error === "timeout" && (
            <Box className="payment-action-buttons">
              <Button
                variant="outlined"
                onClick={() =>
                  navigate("/customer/requests/my-requests", { replace: true })
                }
              >
                {t("payment.goToMyRequests")}
              </Button>
            </Box>
          )}

          {status === "failed" && error !== "timeout" && (
            <Box className="payment-action-buttons">
              <Button
                variant="contained"
                color="primary"
                onClick={handleFailureRedirect}
              >
                {t("payment.tryAgain")}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate("/customer/requests/my-requests", { replace: true })
                }
              >
                {t("Cancel")}
              </Button>
            </Box>
          )}

          {status === "succeeded" && (
            <Button
              variant="text"
              onClick={handleSuccessRedirect}
              className="payment-manual-redirect"
            >
              {t("payment.clickToRedirect")}
            </Button>
          )}
        </Box>

        {/* Footer */}
        <Box className="payment-footer">
          {orderId && (
            <Typography variant="caption" className="payment-order-id">
              {t("payment.reference", { orderId })}
            </Typography>
          )}
          <Typography variant="caption" className="payment-footer-text">
            {t("payment.copyright", { year: new Date().getFullYear() })}
          </Typography>
          <Typography variant="caption" className="payment-footer-text">
            {t("payment.poweredByPaymob")}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentProcessing;
